import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/utils/currency";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Search,
  Plus,
  Edit,
  Trash2,
  Grid,
  List,
  ArrowUp,
  ArrowDown,
  X,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function MenuManagementPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [courseTypes, setCourseTypes] = useState([]);

  // Category modal state
  const [orderInputs, setOrderInputs] = useState({});
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    kot_printer_id: "",
    modifier_groups: [],
    add_ons: [],
  });
  const [printers, setPrinters] = useState([]);
  // Sub-category modal state
  const [subCategoryModalOpen, setSubCategoryModalOpen] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [subCategoryForm, setSubCategoryForm] = useState({ 
    name: "",
    modifier_groups: [],
    add_ons: [],
  });

  // Edit item modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [restaurant, setRestaurant] = useState(null);
  const [commissionRate, setCommissionRate] = useState(0);

  // Add item modal state
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    pricing: { dine_in: "", takeaway: "", delivery: "" },
    tax: "",
    availability: true,
    has_variants: false,
    variants: [],
    order_type_visibility: {
      dine_in: true,
      takeaway: true,
      delivery: true,
    },
    inheritance: {
      category_modifiers: true,
      category_addons: true,
      subcategory_modifiers: true,
      subcategory_addons: true,
    },
    sub_category_id: "",
    modifier_groups: [],
    add_ons: [],
    course_type_id: "",
    prep_time_minutes: 7,
  });

  useEffect(() => {
    fetchCategories();
    fetchCourseTypes();
    fetchRestaurant();
    fetchPrinters();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory.id);
      fetchMenuItems(selectedCategory.id);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      fetchMenuItems(selectedCategory.id, selectedSubCategory?.id);
    }
  }, [selectedSubCategory]);

  const fetchRestaurant = async () => {
    try {
      const response = await axios.get(`${API}/restaurants`, {
        headers: getAuthHeader(),
      });

      if (response.data && response.data.length > 0) {
        const res = response.data[0];
        setRestaurant(res);
        setCommissionRate(Number(res.commission_rate || 0)); // %
      }
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      toast.error("Failed to load restaurant data");
    }
  };

  const normalizeCategories = (data) => {
    return [...data]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((cat, index) => ({
        ...cat,
        ui_order: index + 1,
      }));
  };

  const fetchCategories = async () => {
    const res = await axios.get(`${API}/categories`, {
      headers: getAuthHeader(),
    });

    setCategories(normalizeCategories(res.data));
  };

  useEffect(() => {
    const map = {};
    categories.forEach((c) => {
      map[c.id] = c.ui_order;
    });
    setOrderInputs(map);
  }, [categories]);

  const fetchCourseTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/kitchen/course-types`, {
        headers: getAuthHeader(),
      });
      setCourseTypes(response.data);
    } catch (error) {
      console.error("Error fetching course types:", error);
      // Don't show error toast as course types are optional
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await axios.get(
        `${API}/subcategories?category_id=${categoryId}`,
        {
          headers: getAuthHeader(),
        }
      );
      setSubCategories(response.data);
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
      toast.error("Failed to load sub-categories");
    }
  };

  const fetchMenuItems = async (categoryId, subCategoryId = null) => {
    try {
      let url = `${API}/menu/items?category_id=${categoryId}&include_inherited=false`;
      if (subCategoryId) {
        url += `&sub_category_id=${subCategoryId}`;
      }
      const response = await axios.get(url, {
        headers: getAuthHeader(),
      });
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
      toast.error("Failed to load menu items");
    }
  };

  // Category Functions
  const handleAddCategory = () => {
    setCategoryForm({ 
      name: "",
      kot_printer_id: "",
      modifier_groups: [],
      add_ons: [],
    });
    setEditingCategory(null);
    setCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      kot_printer_id: category.kot_printer_id || "",
      modifier_groups: category.modifier_groups || [],
      add_ons: category.add_ons || [],
    });
    setEditingCategory(category.id);
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory}`, categoryForm, {
          headers: getAuthHeader(),
        });
        toast.success("Category updated successfully");
      } else {
        await axios.post(`${API}/categories`, categoryForm, {
          headers: getAuthHeader(),
        });
        toast.success("Category created successfully");
      }
      setCategoryModalOpen(false);
      setCategoryForm({ name: "", kot_printer_id: "", modifier_groups: [], add_ons: [] });
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Failed to save category");
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  const reorderCategory = async (categoryId, direction) => {
    try {
      await axios.patch(
        `${API}/categories/${categoryId}/reorder?direction=${direction}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Category reordered");
      fetchCategories();
    } catch (error) {
      console.error(error);
      toast.error("Failed to reorder category");
    }
  };

  const updateCategoryPosition = async (categoryId, newPosition) => {
    try {
      await axios.patch(
        `${API}/categories/${categoryId}/reorder?position=${newPosition}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Category position updated");
      fetchCategories();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update position");
    }
  };

  // Sub-Category Functions
  const handleAddSubCategory = () => {
    setSubCategoryForm({ 
      name: "",
      modifier_groups: [],
      add_ons: [],
    });
    setEditingSubCategory(null);
    setSubCategoryModalOpen(true);
  };

  const handleEditSubCategory = (subCategory) => {
    setSubCategoryForm({ 
      name: subCategory.name,
      modifier_groups: subCategory.modifier_groups || [],
      add_ons: subCategory.add_ons || [],
    });
    setEditingSubCategory(subCategory.id);
    setSubCategoryModalOpen(true);
  };

  const handleSaveSubCategory = async () => {
    try {
      const payload = {
        ...subCategoryForm,
        category_id: selectedCategory.id,
      };

      if (editingSubCategory) {
        await axios.put(`${API}/subcategories/${editingSubCategory}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Sub-category updated successfully");
      } else {
        await axios.post(`${API}/subcategories`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Sub-category created successfully");
      }
      setSubCategoryModalOpen(false);
      setSubCategoryForm({ name: "", modifier_groups: [], add_ons: [] });
      setEditingSubCategory(null);
      fetchSubCategories(selectedCategory.id);
    } catch (error) {
      console.error("Error saving sub-category:", error);
      toast.error("Failed to save sub-category");
    }
  };

  const deleteSubCategory = async (subCategoryId) => {
    if (!window.confirm("Are you sure you want to delete this sub-category?"))
      return;

    try {
      await axios.delete(`${API}/subcategories/${subCategoryId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Sub-category deleted successfully");
      fetchSubCategories(selectedCategory.id);
      if (selectedSubCategory?.id === subCategoryId) {
        setSelectedSubCategory(null);
      }
    } catch (error) {
      console.error("Error deleting sub-category:", error);
      toast.error("Failed to delete sub-category");
    }
  };

  // Item Functions
  const handleAddItem = () => {
    setFormData({
      name: "",
      description: "",
      pricing: { dine_in: "", takeaway: "", delivery: "" },
      tax: "",
      availability: true,
      order_type_visibility: {
        dine_in: true,
        takeaway: true,
        delivery: true,
      },
      inheritance: {
        category_modifiers: true,
        category_addons: true,
        subcategory_modifiers: true,
        subcategory_addons: true,
      },
      sub_category_id: selectedSubCategory?.id || "",
      modifier_groups: [],
      add_ons: [],
      course_type_id: "",
      prep_time_minutes: 7,
    });
    setEditingItem(null);
    setAddItemModalOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      pricing: {
        dine_in: removeCommission(item.pricing?.dine_in || item.price || ""),
        takeaway: removeCommission(item.pricing?.takeaway || item.price || ""),
        delivery: removeCommission(item.pricing?.delivery || item.price || ""),
      },
      has_variants: item.pricing?.has_variants || false,
      variants: item.pricing?.variants
        ? item.pricing.variants.map((variant) => ({
            ...variant,
            dine_in: removeCommission(variant.dine_in),
            takeaway: removeCommission(variant.takeaway),
            delivery: removeCommission(variant.delivery),
          }))
        : [],
      tax: item.tax || 0,
      availability: item.availability !== undefined ? item.availability : true,
      order_type_visibility: item.order_type_visibility || {
        dine_in: true,
        takeaway: true,
        delivery: true,
      },
      inheritance: item.inheritance || {
        category_modifiers: true,
        category_addons: true,
        subcategory_modifiers: true,
        subcategory_addons: true,
      },
      sub_category_id: item.sub_category_id || "",
      modifier_groups: item.modifier_groups || [],
      add_ons: item.add_ons || [],
      course_type_id: item.course_type_id || "",
      prep_time_minutes: item.prep_time_minutes || 7,
    });
    setEditModalOpen(true);
  };

  const removeCommission = (price) => {
    const p = parseFloat(price) || 0;
    if (!commissionRate) return +p.toFixed(2);
    return +(p / (1 + commissionRate / 100)).toFixed(2);
  };

  const applyCommission = (price) => {
    const p = parseFloat(price) || 0;
    return +(p + (p * commissionRate) / 100).toFixed(2);
  };

  const handleSaveItem = async () => {
    try {
      if (!formData.name?.trim()) {
        toast.error(`Item name is required`);
        return;
      }

// 🔴 PRICING VALIDATION

const orderTypes = [
  "dine_in",
  "takeaway",
  "delivery",
];

// 🔴 CHECK AT LEAST ONE ORDER TYPE IS VISIBLE
const hasVisibleOrderType = orderTypes.some(
  (type) => formData.order_type_visibility[type]
);

if (!hasVisibleOrderType) {
  toast.error(
    `Item must be visible for at least one order type`
  );
  return;
}

// ======================================================
// 🔴 VARIANT VALIDATION
// ======================================================

if (formData.has_variants) {

  // At least 1 variant required
  if (
    !formData.variants ||
    formData.variants.length === 0
  ) {
    toast.error(
      "Please add at least one variant"
    );
    return;
  }

  for (
    let i = 0;
    i < formData.variants.length;
    i++
  ) {
    const variant = formData.variants[i];

    // Variant name required
    if (!variant.name?.trim()) {
      toast.error(
        `Variant ${i + 1}: Name is required`
      );
      return;
    }

    // Validate ONLY visible order types
    for (let field of orderTypes) {

      // Skip hidden order types
      if (
        !formData.order_type_visibility[field]
      ) {
        continue;
      }

      const value = variant[field];

      if (
        value === "" ||
        value === undefined ||
        isNaN(value) ||
        Number(value) < 0
      ) {
        toast.error(
          `Variant "${variant.name}" → Invalid ${field.replace(
            "_",
            " "
          )} price`
        );
        return;
      }
    }
  }

} else {

  // ======================================================
  // 🔴 NORMAL PRICE VALIDATION
  // ======================================================

  for (let field of orderTypes) {

    // Skip hidden order types
    if (
      !formData.order_type_visibility[field]
    ) {
      continue;
    }

    const value = formData.pricing[field];

    if (
      value === "" ||
      value === undefined ||
      isNaN(value) ||
      Number(value) < 0
    ) {
      toast.error(
        `Please enter valid ${field.replace(
          "_",
          " "
        )} price`
      );
      return;
    }
  }
}

      // 🔴 MODIFIER GROUP VALIDATION
      for (let i = 0; i < formData.modifier_groups.length; i++) {
        const group = formData.modifier_groups[i];

        // 1️⃣ Group title required
        if (!group.name?.trim()) {
          toast.error(`Modifier group ${i + 1}: Group name is required`);
          return;
        }

        // 2️⃣ At least one option required
        if (!group.options || group.options.length === 0) {
          toast.error(
            `Modifier group "${group.name}" must have at least one option`
          );
          return;
        }

        // 3️⃣ Option name required
        for (let j = 0; j < group.options.length; j++) {
          const option = group.options[j];

          if (!option.name?.trim()) {
            toast.error(
              `Modifier group "${group.name}": Option ${j + 1} name is required`
            );
            return;
          }
        }
      }

      // 🔴 ADD-ONS VALIDATION
      if (formData.add_ons?.length > 0) {
        for (let i = 0; i < formData.add_ons.length; i++) {
          const addOn = formData.add_ons[i];

          // 1️⃣ Add-on name required
          if (!addOn.name?.trim()) {
            toast.error(`Add-On ${i + 1}: Name is required`);
            return;
          }

          // 2️⃣ Add-on price validation
          if (
            addOn.price === "" ||
            isNaN(addOn.price) ||
            Number(addOn.price) < 0
          ) {
            toast.error(`Add-On "${addOn.name || i + 1}": Invalid price`);
            return;
          }
        }
      }

      // ✅ PAYLOAD (same as your code)
      const payload = {
        ...formData,
        category_id: selectedCategory.id,
        sub_category_id: formData.sub_category_id || null,
        pricing: {
          dine_in: applyCommission(formData.pricing.dine_in),
          takeaway: applyCommission(formData.pricing.takeaway),
          delivery: applyCommission(formData.pricing.delivery),
                    has_variants: formData.has_variants,

  variants: formData.has_variants
    ? formData.variants.map((variant) => ({
        ...variant,

        dine_in: applyCommission(
          variant.dine_in
        ),

        takeaway: applyCommission(
          variant.takeaway
        ),

        delivery: applyCommission(
          variant.delivery
        ),
      }))
    : [],
        },
        tax: parseFloat(formData.tax) || 0,
        course_type_id: formData.course_type_id || null,
        prep_time_minutes: parseInt(formData.prep_time_minutes) || 7,
        inheritance: {
          category_modifiers: formData.inheritance?.category_modifiers ?? true,
          category_addons: formData.inheritance?.category_addons ?? true,
          subcategory_modifiers: formData.inheritance?.subcategory_modifiers ?? true,
          subcategory_addons: formData.inheritance?.subcategory_addons ?? true,
        },
      };

      if (editingItem) {
        await axios.put(`${API}/menu/item/${editingItem.id}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Item updated successfully");
        setEditModalOpen(false);
      } else {
        await axios.post(`${API}/menu/item`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Item created successfully");
        setAddItemModalOpen(false);
      }

      fetchMenuItems(selectedCategory.id, selectedSubCategory?.id);
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("Failed to save item");
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await axios.delete(`${API}/menu/item/${itemId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Item deleted successfully");
      fetchMenuItems(selectedCategory.id);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const reorderItem = async (itemId, direction) => {
    try {
      await axios.patch(
        `${API}/menu/item/${itemId}/reorder?direction=${direction}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Item reordered");
      fetchMenuItems(selectedCategory.id);
    } catch (error) {
      console.error("Error reordering item:", error);
      toast.error("Failed to reorder item");
    }
  };

  const updateItemPosition = async (itemId, newPosition) => {
    try {
      await axios.patch(
        `${API}/menu/item/${itemId}/reorder?position=${newPosition}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Item position updated");
      fetchMenuItems(selectedCategory.id);
    } catch (error) {
      console.error("Error updating position:", error);
      toast.error("Failed to update position");
    }
  };

  const fetchPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers/configured`, {
        headers: getAuthHeader(),
      });
      setPrinters(response.data.printers || []);
    } catch (error) {
      console.error("Error fetching printers:", error);
      setPrinters([]);
    }
  };

  // Modifier Group Functions
  const addModifierGroup = () => {
    setFormData({
      ...formData,
      modifier_groups: [
        ...formData.modifier_groups,
        { name: "", required: false, multi_select: false, options: [] },
      ],
    });
  };

  const updateModifierGroup = (index, field, value) => {
    const updated = [...formData.modifier_groups];
    updated[index][field] = value;
    setFormData({ ...formData, modifier_groups: updated });
  };

  const removeModifierGroup = (index) => {
    setFormData({
      ...formData,
      modifier_groups: formData.modifier_groups.filter((_, i) => i !== index),
    });
  };

  const addModifierOption = (groupIndex) => {
    const updated = [...formData.modifier_groups];
    updated[groupIndex].options.push({ name: "", price: 0 });
    setFormData({ ...formData, modifier_groups: updated });
  };

  const updateModifierOption = (groupIndex, optionIndex, field, value) => {
    const updated = [...formData.modifier_groups];
    updated[groupIndex].options[optionIndex][field] = value;
    setFormData({ ...formData, modifier_groups: updated });
  };

  const removeModifierOption = (groupIndex, optionIndex) => {
    const updated = [...formData.modifier_groups];
    updated[groupIndex].options = updated[groupIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    setFormData({ ...formData, modifier_groups: updated });
  };

  // Add-On Functions
  const addAddOn = () => {
    setFormData({
      ...formData,
      add_ons: [...formData.add_ons, { name: "", price: 0 }],
    });
  };

  const updateAddOn = (index, field, value) => {
    const updated = [...formData.add_ons];
    updated[index][field] = value;
    setFormData({ ...formData, add_ons: updated });
  };

  const removeAddOn = (index) => {
    setFormData({
      ...formData,
      add_ons: formData.add_ons.filter((_, i) => i !== index),
    });
  };

  // Category Modifier Group Functions
  const addCategoryModifierGroup = () => {
    setCategoryForm({
      ...categoryForm,
      modifier_groups: [
        ...categoryForm.modifier_groups,
        { name: "", required: false, multi_select: false, options: [] },
      ],
    });
  };

  const updateCategoryModifierGroup = (index, field, value) => {
    const updated = [...categoryForm.modifier_groups];
    updated[index][field] = value;
    setCategoryForm({ ...categoryForm, modifier_groups: updated });
  };

  const removeCategoryModifierGroup = (index) => {
    setCategoryForm({
      ...categoryForm,
      modifier_groups: categoryForm.modifier_groups.filter((_, i) => i !== index),
    });
  };

  const addCategoryModifierOption = (groupIndex) => {
    const updated = [...categoryForm.modifier_groups];
    updated[groupIndex].options.push({ name: "", price: 0 });
    setCategoryForm({ ...categoryForm, modifier_groups: updated });
  };

  const updateCategoryModifierOption = (groupIndex, optionIndex, field, value) => {
    const updated = [...categoryForm.modifier_groups];
    updated[groupIndex].options[optionIndex][field] = value;
    setCategoryForm({ ...categoryForm, modifier_groups: updated });
  };

  const removeCategoryModifierOption = (groupIndex, optionIndex) => {
    const updated = [...categoryForm.modifier_groups];
    updated[groupIndex].options = updated[groupIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    setCategoryForm({ ...categoryForm, modifier_groups: updated });
  };

  // Category Add-On Functions
  const addCategoryAddOn = () => {
    setCategoryForm({
      ...categoryForm,
      add_ons: [...categoryForm.add_ons, { name: "", price: 0 }],
    });
  };

  const updateCategoryAddOn = (index, field, value) => {
    const updated = [...categoryForm.add_ons];
    updated[index][field] = value;
    setCategoryForm({ ...categoryForm, add_ons: updated });
  };

  const removeCategoryAddOn = (index) => {
    setCategoryForm({
      ...categoryForm,
      add_ons: categoryForm.add_ons.filter((_, i) => i !== index),
    });
  };

  // SubCategory Modifier Group Functions
  const addSubCategoryModifierGroup = () => {
    setSubCategoryForm({
      ...subCategoryForm,
      modifier_groups: [
        ...subCategoryForm.modifier_groups,
        { name: "", required: false, multi_select: false, options: [] },
      ],
    });
  };

  const updateSubCategoryModifierGroup = (index, field, value) => {
    const updated = [...subCategoryForm.modifier_groups];
    updated[index][field] = value;
    setSubCategoryForm({ ...subCategoryForm, modifier_groups: updated });
  };

  const removeSubCategoryModifierGroup = (index) => {
    setSubCategoryForm({
      ...subCategoryForm,
      modifier_groups: subCategoryForm.modifier_groups.filter((_, i) => i !== index),
    });
  };

  const addSubCategoryModifierOption = (groupIndex) => {
    const updated = [...subCategoryForm.modifier_groups];
    updated[groupIndex].options.push({ name: "", price: 0 });
    setSubCategoryForm({ ...subCategoryForm, modifier_groups: updated });
  };

  const updateSubCategoryModifierOption = (groupIndex, optionIndex, field, value) => {
    const updated = [...subCategoryForm.modifier_groups];
    updated[groupIndex].options[optionIndex][field] = value;
    setSubCategoryForm({ ...subCategoryForm, modifier_groups: updated });
  };

  const removeSubCategoryModifierOption = (groupIndex, optionIndex) => {
    const updated = [...subCategoryForm.modifier_groups];
    updated[groupIndex].options = updated[groupIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    setSubCategoryForm({ ...subCategoryForm, modifier_groups: updated });
  };

  // SubCategory Add-On Functions
  const addSubCategoryAddOn = () => {
    setSubCategoryForm({
      ...subCategoryForm,
      add_ons: [...subCategoryForm.add_ons, { name: "", price: 0 }],
    });
  };

  const updateSubCategoryAddOn = (index, field, value) => {
    const updated = [...subCategoryForm.add_ons];
    updated[index][field] = value;
    setSubCategoryForm({ ...subCategoryForm, add_ons: updated });
  };

  const removeSubCategoryAddOn = (index) => {
    setSubCategoryForm({
      ...subCategoryForm,
      add_ons: subCategoryForm.add_ons.filter((_, i) => i !== index),
    });
  };

  const filteredItems = menuItems.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Item Form Component (reusable for both Add and Edit)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  Menu Management
                </h1>
                {selectedCategory && (
                  <p className="text-sm text-gray-600">
                    Category: {selectedCategory.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {!selectedCategory ? (
          /* Categories Grid */
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Categories
              </h2>
              <Button
                onClick={handleAddCategory}
                className="bg-red-600 hover:bg-red-700"
              >
                <Plus size={16} className="mr-2" />
                Add Category
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category, index) => (
                <Card
                  key={category.id}
                  className="hover:shadow-lg transition-all"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3 flex-1">
                        <Input
                          type="number"
                          min={1}
                          max={categories.length}
                          value={orderInputs[category.id] ?? category.ui_order}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (Number.isNaN(value)) return;

                            setOrderInputs((prev) => ({
                              ...prev,
                              [category.id]: value,
                            }));
                          }}
                          onBlur={async () => {
                            const newPosition = orderInputs[category.id];

                            if (
                              newPosition < 1 ||
                              newPosition > categories.length ||
                              newPosition === category.ui_order
                            )
                              return;

                            try {
                              await updateCategoryPosition(
                                category.id,
                                newPosition
                              );
                              toast.success("Position updated");
                            } catch {
                              toast.error("Failed to update position");
                            }
                          }}
                          className="w-14 text-center text-xs font-bold"
                        />

                        <CardTitle className="text-lg">
                          {category.name}
                        </CardTitle>
                      </div>

                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            reorderCategory(category.id, "up");
                          }}
                        >
                          <ArrowUp size={14} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === categories.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            reorderCategory(category.id, "down");
                          }}
                        >
                          <ArrowDown size={14} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category);
                          }}
                        >
                          <Edit size={14} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCategory(category.id);
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <div className="text-[0.8rem] text-black ml-6 mb-2 font-semibold">
                    Items : {category.item_count}
                  </div>
                  {category.kot_printer_id && (
                    <CardContent>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">KOT Printer:</span>{" "}
                        {(Array.isArray(printers) &&
                          printers.find(
                            (p) => p.printer_id === category.kot_printer_id
                          )?.printer_name) ||
                          category.kot_printer_id.substring(0, 8)}
                      </div>
                    </CardContent>
                  )}
                  <CardContent>
                    <div
                      className="text-sm text-blue-600 cursor-pointer font-semibold underline"
                      onClick={() => setSelectedCategory(category)}
                    >
                      Click to view items
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          /* Items View */
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedCategory(null)}
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Back to Categories
                </Button>
                <h2 className="text-xl font-semibold text-gray-800">
                  {selectedCategory.name} Items
                </h2>
              </div>
              <div className="flex gap-3">
                <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                  <Button
                    variant={viewMode === "grid" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("grid")}
                  >
                    <Grid size={16} />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                  >
                    <List size={16} />
                  </Button>
                </div>
                <Button
                  onClick={handleAddItem}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus size={16} className="mr-2" />
                  Add New Item
                </Button>
              </div>
            </div>

            {/* Sub-Categories Section */}
            <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  Sub-Categories (Optional)
                </h3>
                <Button
                  onClick={handleAddSubCategory}
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <Plus size={14} className="mr-1" />
                  Add Sub-Category
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setSelectedSubCategory(null)}
                  variant={selectedSubCategory === null ? "default" : "outline"}
                  size="sm"
                  className={
                    selectedSubCategory === null
                      ? "bg-red-600 hover:bg-red-700"
                      : ""
                  }
                >
                  All Items
                </Button>
                {subCategories.map((subCat) => (
                  <div key={subCat.id} className="relative group">
                    <Button
                      onClick={() => setSelectedSubCategory(subCat)}
                      variant={
                        selectedSubCategory?.id === subCat.id
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      className={
                        selectedSubCategory?.id === subCat.id
                          ? "bg-red-600 hover:bg-red-700 pr-8"
                          : "pr-8"
                      }
                    >
                      {subCat.name}
                    </Button>
                    <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSubCategory(subCat);
                        }}
                      >
                        <Edit size={12} className="text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSubCategory(subCat.id);
                        }}
                      >
                        <Trash2 size={12} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {subCategories.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  No sub-categories yet. Sub-categories help organize items
                  within this category.
                </p>
              )}
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <Input
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Items Display */}
            {viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item, index) => (
                  <Card
                    key={item.id}
                    className="hover:shadow-lg transition-all"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="number"
                            min="1"
                            max={filteredItems.length}
                            value={index + 1}
                            onChange={(e) => {
                              const newPos = parseInt(e.target.value);
                              if (
                                newPos > 0 &&
                                newPos <= filteredItems.length
                              ) {
                                updateItemPosition(item.id, newPos);
                              }
                            }}
                            className="w-14 text-center text-xs font-bold"
                            title="Position"
                          />
                          <CardTitle className="text-base">
                            {item.name}
                          </CardTitle>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reorderItem(item.id, "up")}
                            disabled={index === 0}
                            title="Move Up"
                          >
                            <ArrowUp size={14} className="text-gray-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => reorderItem(item.id, "down")}
                            disabled={index === filteredItems.length - 1}
                            title="Move Down"
                          >
                            <ArrowDown size={14} className="text-gray-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditItem(item)}
                          >
                            <Edit size={14} className="text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteItem(item.id)}
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {item.description || "No description"}
                      </p>
                      <div className="flex justify-between items-center">
                        {!item.pricing?.has_variants &&
                        <div>
                          <p className="text-xs text-gray-500">Dine-in</p>
                          <p className="font-bold text-lg text-red-600">
                            {formatCurrency(item.pricing?.dine_in || item.price || 0)}
                          </p>
                        </div>}
                        <div
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.availability
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.availability ? "Available" : "Unavailable"}
                        </div>
                      </div>
                      {/* ORDER TYPE VISIBILITY DISPLAY */}

                      {item.pricing?.has_variants &&
                        item.pricing?.variants?.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-500 mb-2">
                              Variants
                            </p>
                            <div className="space-y-2">
                              {item.pricing.variants.map((variant, idx) => (
                                <div
                                  key={idx}
                                  className="flex justify-between items-center text-sm text-gray-700"
                                >
                                  <span>{variant.name || "Unnamed"}</span>
                                  <span className="font-semibold text-slate-900">
                                    {formatCurrency(
                                      variant.dine_in || variant.price || 0
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-500 mb-2">Visible In</p>

                        <div className="flex flex-wrap gap-2">
                          {item.order_type_visibility?.dine_in && (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                              Dine-In
                            </span>
                          )}

                          {item.order_type_visibility?.takeaway && (
                            <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
                              Takeaway
                            </span>
                          )}

                          {item.order_type_visibility?.delivery && (
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                              Delivery
                            </span>
                          )}

                          {!item.order_type_visibility?.dine_in &&
                            !item.order_type_visibility?.takeaway &&
                            !item.order_type_visibility?.delivery && (
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                Hidden
                              </span>
                            )}
                        </div>
                      </div>
                      {item.modifier_groups &&
                        item.modifier_groups.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-500">
                              {item.modifier_groups.length} modifier group(s)
                            </p>
                          </div>
                        )}
                      {item.add_ons && item.add_ons.length > 0 && (
                        <div className="mt-1">
                          <p className="text-xs text-gray-500">
                            {item.add_ons.length} add-on(s)
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="bg-white rounded-lg shadow">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Modifiers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Visible In
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredItems.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              min="1"
                              max={filteredItems.length}
                              value={index + 1}
                              onChange={(e) => {
                                const newPos = parseInt(e.target.value);
                                if (
                                  newPos > 0 &&
                                  newPos <= filteredItems.length
                                ) {
                                  updateItemPosition(item.id, newPos);
                                }
                              }}
                              className="w-14 text-center text-xs font-bold"
                              title="Position"
                            />
                            <div>
                              <div className="font-medium text-sm">
                                {item.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-xs">
                                {item.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-red-600">
                          {formatCurrency(item.pricing?.dine_in || item.price || 0)}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {(item.modifier_groups?.length || 0) +
                            (item.add_ons?.length || 0)}{" "}
                          total
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              item.availability
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {item.availability ? "Available" : "Unavailable"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {item.order_type_visibility?.dine_in && (
                              <span className="px-2 py-1 text-[10px] rounded-full bg-blue-100 text-blue-700">
                                Dine-In
                              </span>
                            )}

                            {item.order_type_visibility?.takeaway && (
                              <span className="px-2 py-1 text-[10px] rounded-full bg-orange-100 text-orange-700">
                                Takeaway
                              </span>
                            )}

                            {item.order_type_visibility?.delivery && (
                              <span className="px-2 py-1 text-[10px] rounded-full bg-purple-100 text-purple-700">
                                Online
                              </span>
                            )}

                            {!item.order_type_visibility?.dine_in &&
                              !item.order_type_visibility?.takeaway &&
                              !item.order_type_visibility?.delivery && (
                                <span className="px-2 py-1 text-[10px] rounded-full bg-gray-100 text-gray-600">
                                  Hidden
                                </span>
                              )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => reorderItem(item.id, "up")}
                              disabled={index === 0}
                            >
                              <ArrowUp size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => reorderItem(item.id, "down")}
                              disabled={index === filteredItems.length - 1}
                            >
                              <ArrowDown size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditItem(item)}
                            >
                              <Edit size={14} className="text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteItem(item.id)}
                            >
                              <Trash2 size={14} className="text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Enter category name"
              />
            </div>
            
            {/* Modifier Groups */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Modifier Groups</h3>
                <Button type="button" size="sm" onClick={addCategoryModifierGroup}>
                  <Plus size={16} className="mr-2" />
                  Add Group
                </Button>
              </div>

              {categoryForm.modifier_groups.map((group, groupIndex) => (
                <Card key={`cat-group-${groupIndex}`} className="mb-4">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <Input
                            placeholder="Group name (e.g., Spice Level)"
                            value={group.name}
                            onChange={(e) =>
                              updateCategoryModifierGroup(
                                groupIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.required}
                                onChange={(e) =>
                                  updateCategoryModifierGroup(
                                    groupIndex,
                                    "required",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Required</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.multi_select}
                                onChange={(e) =>
                                  updateCategoryModifierGroup(
                                    groupIndex,
                                    "multi_select",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Multi-select</span>
                            </label>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCategoryModifierGroup(groupIndex)}
                        >
                          <X size={16} />
                        </Button>
                      </div>

                      {/* Modifier Options */}
                      <div className="ml-4 space-y-2">
                        {group.options.map((option, optionIndex) => (
                          <div
                            key={`cat-option-${groupIndex}-${optionIndex}`}
                            className="flex gap-2"
                          >
                            <Input
                              placeholder="Option name"
                              value={option.name}
                              onChange={(e) =>
                                updateCategoryModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={option.price}
                              onChange={(e) =>
                                updateCategoryModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "price",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-24"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeCategoryModifierOption(groupIndex, optionIndex)
                              }
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addCategoryModifierOption(groupIndex)}
                        >
                          <Plus size={14} className="mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Add-Ons */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Add-Ons</h3>
                <Button type="button" size="sm" onClick={addCategoryAddOn}>
                  <Plus size={16} className="mr-2" />
                  Add Add-On
                </Button>
              </div>

              <div className="space-y-2">
                {categoryForm.add_ons.map((addOn, index) => (
                  <div key={`cat-addon-${index}`} className="flex gap-2">
                    <Input
                      placeholder="Add-on name"
                      value={addOn.name}
                      onChange={(e) =>
                        updateCategoryAddOn(index, "name", e.target.value)
                      }
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={addOn.price}
                      onChange={(e) =>
                        updateCategoryAddOn(
                          index,
                          "price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCategoryAddOn(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label htmlFor="kot_printer">Assign KOT Printer (Optional)</Label>
              <select
                id="kot_printer"
                value={categoryForm.kot_printer_id}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    kot_printer_id: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Default Printer --</option>
                {Array.isArray(printers) &&
                  printers
                    .filter((p) => p.printer_type === "kot" && p.is_active)
                    .map((printer) => (
                      <option
                        key={printer.printer_id}
                        value={printer.printer_id}
                      >
                        {printer.printer_name || printer.printer_id}
                      </option>
                    ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Items in this category will print to this specific KOT printer
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setCategoryModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                className="bg-red-600 hover:bg-red-700"
              >
                {editingCategory ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Sub-Category Modal */}
      <Dialog
        open={subCategoryModalOpen}
        onOpenChange={setSubCategoryModalOpen}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSubCategory ? "Edit Sub-Category" : "Add Sub-Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sub-Category Name *</Label>
              <Input
                value={subCategoryForm.name}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                placeholder="Enter sub-category name"
              />
              <p className="text-xs text-gray-500 mt-1">
                For category: {selectedCategory?.name}
              </p>
            </div>

            {/* Modifier Groups */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Modifier Groups</h3>
                <Button type="button" size="sm" onClick={addSubCategoryModifierGroup}>
                  <Plus size={16} className="mr-2" />
                  Add Group
                </Button>
              </div>

              {subCategoryForm.modifier_groups.map((group, groupIndex) => (
                <Card key={`subcat-group-${groupIndex}`} className="mb-4">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <Input
                            placeholder="Group name (e.g., Spice Level)"
                            value={group.name}
                            onChange={(e) =>
                              updateSubCategoryModifierGroup(
                                groupIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.required}
                                onChange={(e) =>
                                  updateSubCategoryModifierGroup(
                                    groupIndex,
                                    "required",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Required</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.multi_select}
                                onChange={(e) =>
                                  updateSubCategoryModifierGroup(
                                    groupIndex,
                                    "multi_select",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Multi-select</span>
                            </label>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubCategoryModifierGroup(groupIndex)}
                        >
                          <X size={16} />
                        </Button>
                      </div>

                      <div className="ml-4 space-y-2">
                        {group.options.map((option, optionIndex) => (
                          <div
                            key={`subcat-option-${groupIndex}-${optionIndex}`}
                            className="flex gap-2"
                          >
                            <Input
                              placeholder="Option name"
                              value={option.name}
                              onChange={(e) =>
                                updateSubCategoryModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={option.price}
                              onChange={(e) =>
                                updateSubCategoryModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "price",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-24"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeSubCategoryModifierOption(groupIndex, optionIndex)
                              }
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSubCategoryModifierOption(groupIndex)}
                        >
                          <Plus size={14} className="mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Add-Ons */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Add-Ons</h3>
                <Button type="button" size="sm" onClick={addSubCategoryAddOn}>
                  <Plus size={16} className="mr-2" />
                  Add Add-On
                </Button>
              </div>

              <div className="space-y-2">
                {subCategoryForm.add_ons.map((addOn, index) => (
                  <div key={`subcat-addon-${index}`} className="flex gap-2">
                    <Input
                      placeholder="Add-on name"
                      value={addOn.name}
                      onChange={(e) =>
                        updateSubCategoryAddOn(index, "name", e.target.value)
                      }
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={addOn.price}
                      onChange={(e) =>
                        updateSubCategoryAddOn(
                          index,
                          "price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubCategoryAddOn(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setSubCategoryModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSubCategory}
                className="bg-red-600 hover:bg-red-700"
              >
                {editingSubCategory ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Item Modal */}
      <Dialog open={addItemModalOpen} onOpenChange={setAddItemModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Menu Item</DialogTitle>
          </DialogHeader>

          {/* Item Form - Inlined to prevent focus loss */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter item name"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter item description"
                  rows={3}
                />
              </div>

              <div>
                <Label>Sub-Category (Optional)</Label>
                <select
                  value={formData.sub_category_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sub_category_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">No Sub-Category</option>
                  {subCategories.map((subCat) => (
                    <option key={subCat.id} value={subCat.id}>
                      {subCat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Assign this item to a sub-category for better organization
                </p>
              </div>
                  <div className="flex items-center gap-2">
  <input
    type="checkbox"
    checked={formData.has_variants}
onChange={(e) => {
  const checked = e.target.checked;

  setFormData({
    ...formData,

    has_variants: checked,

    variants: checked
      ? formData.variants || [
          {
            name: "",
            dine_in: "",
            takeaway: "",
            delivery: "",
          },
        ]
      : [],
  });
}}
  />

  <Label>
    This item has multiple sizes/prices
  </Label>
</div>
              {/* Pricing */}
{!formData.has_variants ? (
  <div className="grid grid-cols-3 gap-4">

    {/* DINE IN */}
    <div>
      <Label>Dine-in Price</Label>

      <Input
        type="number"
        step="0.01"
        value={formData.pricing.dine_in}
        onChange={(e) =>
          setFormData({
            ...formData,
            pricing: {
              ...formData.pricing,
              dine_in: e.target.value,
            },
          })
        }
      />
      {formData.pricing.dine_in !== "" && (
        <p className="text-xs text-slate-500 mt-1">
          Commission included: {formatCurrency(applyCommission(formData.pricing.dine_in))}
        </p>
      )}
    </div>

    {/* TAKEAWAY */}
    <div>
      <Label>Takeaway Price</Label>

      <Input
        type="number"
        step="0.01"
        value={formData.pricing.takeaway}
        onChange={(e) =>
          setFormData({
            ...formData,
            pricing: {
              ...formData.pricing,
              takeaway: e.target.value,
            },
          })
        }
      />
      {formData.pricing.takeaway !== "" && (
        <p className="text-xs text-slate-500 mt-1">
          Commission included: {formatCurrency(applyCommission(formData.pricing.takeaway))}
        </p>
      )}
    </div>

    {/* DELIVERY */}
    <div>
      <Label>Online Price</Label>

      <Input
        type="number"
        step="0.01"
        value={formData.pricing.delivery}
        onChange={(e) =>
          setFormData({
            ...formData,
            pricing: {
              ...formData.pricing,
              delivery: e.target.value,
            },
          })
        }
      />
      {formData.pricing.delivery !== "" && (
        <p className="text-xs text-slate-500 mt-1">
          Commission included: {formatCurrency(applyCommission(formData.pricing.delivery))}
        </p>
      )}
    </div>
  </div>
) : (
  <div className="space-y-4">

    {(formData.variants || []).map((variant, index) => (
      <div
        key={index}
        className="border rounded-lg p-4"
      >
        <div className="grid grid-cols-4 gap-3">

          {/* NAME */}
          <Input
            placeholder="Size / Quantity"
            value={variant.name}
            onChange={(e) => {
              const updated = [...formData.variants];

              updated[index].name = e.target.value;

              setFormData({
                ...formData,
                variants: updated,
              });
            }}
          />

          {/* DINE IN */}
          <Input
            type="number"
            placeholder="Dine In"
            value={variant.dine_in}
            onChange={(e) => {
              const updated = [...formData.variants];

              updated[index].dine_in =
                e.target.value;

              setFormData({
                ...formData,
                variants: updated,
              });
            }}
          />

          {/* TAKEAWAY */}
          <Input
            type="number"
            placeholder="Takeaway"
            value={variant.takeaway}
            onChange={(e) => {
              const updated = [...formData.variants];

              updated[index].takeaway =
                e.target.value;

              setFormData({
                ...formData,
                variants: updated,
              });
            }}
          />

          {/* DELIVERY */}
          <Input
            type="number"
            placeholder="Delivery"
            value={variant.delivery}
            onChange={(e) => {
              const updated = [...formData.variants];

              updated[index].delivery =
                e.target.value;

              setFormData({
                ...formData,
                variants: updated,
              });
            }}
          />
        </div>
        <div className="grid grid-cols-4 gap-3 text-xs text-slate-500 mt-2">
          <div />
          <div>
            {variant.dine_in !== "" && (
              <p>
                Commission included: {formatCurrency(applyCommission(variant.dine_in))}
              </p>
            )}
          </div>
          <div>
            {variant.takeaway !== "" && (
              <p>
                Commission included: {formatCurrency(applyCommission(variant.takeaway))}
              </p>
            )}
          </div>
          <div>
            {variant.delivery !== "" && (
              <p>
                Commission included: {formatCurrency(applyCommission(variant.delivery))}
              </p>
            )}
          </div>
        </div>

        <Button
          type="button"
          variant="destructive"
          className="mt-2"
          onClick={() => {
            const updated =
              formData.variants.filter(
                (_, i) => i !== index
              );

            setFormData({
              ...formData,
              variants: updated,
            });
          }}
        >
          Remove
        </Button>
      </div>
    ))}

    <Button
      type="button"
      onClick={() =>
        setFormData({
          ...formData,

          variants: [
            ...formData.variants,

            {
              name: "",
              dine_in: "",
              takeaway: "",
              delivery: "",
            },
          ],
        })
      }
    >
      Add Variant
    </Button>
  </div>
)}

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Tax (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax}
                    onChange={(e) =>
                      setFormData({ ...formData, tax: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="add-availability"
                    checked={formData.availability}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        availability: e.target.checked,
                      })
                    }
                    className="w-4 h-4"
                  />
                  <Label htmlFor="add-availability">Available</Label>
                </div>
              </div>
              {/* Order Type Visibility */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Order Type Visibility</h3>

                <div className="grid grid-cols-3 gap-4">
                  {/* Dine In */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility.dine_in}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            dine_in: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Dine-In</span>
                  </label>

                  {/* Takeaway */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility.takeaway}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            takeaway: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Takeaway</span>
                  </label>

                  {/* Delivery */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility.delivery}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            delivery: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Online</span>
                  </label>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Select where this item should appear in POS.
                </p>
              </div>
              {/* Inheritance Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Inherit Modifiers & Add-Ons</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Category Modifiers */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.inheritance?.category_modifiers ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inheritance: {
                            ...formData.inheritance,
                            category_modifiers: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Category Modifiers</span>
                  </label>

                  {/* Category Add-Ons */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.inheritance?.category_addons ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inheritance: {
                            ...formData.inheritance,
                            category_addons: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Category Add-Ons</span>
                  </label>

                  {/* Sub-Category Modifiers */}
                  {formData.sub_category_id && (
                    <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.inheritance?.subcategory_modifiers ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            inheritance: {
                              ...formData.inheritance,
                              subcategory_modifiers: e.target.checked,
                            },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Sub-Category Modifiers</span>
                    </label>
                  )}

                  {/* Sub-Category Add-Ons */}
                  {formData.sub_category_id && (
                    <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.inheritance?.subcategory_addons ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            inheritance: {
                              ...formData.inheritance,
                              subcategory_addons: e.target.checked,
                            },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Sub-Category Add-Ons</span>
                    </label>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Allow this item to inherit modifiers and add-ons from its category/sub-category.
                </p>
              </div>
              {/* Kitchen Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Kitchen Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Course Type</Label>
                    <select
                      value={formData.course_type_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          course_type_id: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select course type</option>
                      {courseTypes.map((ct) => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Prep Time (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="120"
                      value={formData.prep_time_minutes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          prep_time_minutes: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modifier Groups */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Modifier Groups</h3>
                <Button type="button" size="sm" onClick={addModifierGroup}>
                  <Plus size={16} className="mr-2" />
                  Add Group
                </Button>
              </div>

              {formData.modifier_groups.map((group, groupIndex) => (
                <Card key={`add-group-${groupIndex}`} className="mb-4">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <Input
                            placeholder="Group name (e.g., Spice Level)"
                            value={group.name}
                            onChange={(e) =>
                              updateModifierGroup(
                                groupIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.required}
                                onChange={(e) =>
                                  updateModifierGroup(
                                    groupIndex,
                                    "required",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Required</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.multi_select}
                                onChange={(e) =>
                                  updateModifierGroup(
                                    groupIndex,
                                    "multi_select",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Multi-select</span>
                            </label>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeModifierGroup(groupIndex)}
                        >
                          <X size={16} />
                        </Button>
                      </div>

                      {/* Modifier Options */}
                      <div className="ml-4 space-y-2">
                        {group.options.map((option, optionIndex) => (
                          <div
                            key={`add-option-${groupIndex}-${optionIndex}`}
                            className="flex gap-2"
                          >
                            <Input
                              placeholder="Option name"
                              value={option.name}
                              onChange={(e) =>
                                updateModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={option.price}
                              onChange={(e) =>
                                updateModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "price",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-24"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeModifierOption(groupIndex, optionIndex)
                              }
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addModifierOption(groupIndex)}
                        >
                          <Plus size={14} className="mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Add-Ons */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Add-Ons</h3>
                <Button type="button" size="sm" onClick={addAddOn}>
                  <Plus size={16} className="mr-2" />
                  Add Add-On
                </Button>
              </div>

              <div className="space-y-2">
                {formData.add_ons.map((addOn, index) => (
                  <div key={`add-addon-${index}`} className="flex gap-2">
                    <Input
                      placeholder="Add-on name"
                      value={addOn.name}
                      onChange={(e) =>
                        updateAddOn(index, "name", e.target.value)
                      }
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={addOn.price}
                      onChange={(e) =>
                        updateAddOn(
                          index,
                          "price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAddOn(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setAddItemModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              className="bg-red-600 hover:bg-red-700"
            >
              Create Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Item Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Menu Item</DialogTitle>
          </DialogHeader>

          {/* Item Form - Inlined to prevent focus loss */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter item name"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter item description"
                  rows={3}
                />
              </div>

              <div>
                <Label>Sub-Category (Optional)</Label>
                <select
                  value={formData.sub_category_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sub_category_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">No Sub-Category</option>
                  {subCategories.map((subCat) => (
                    <option key={subCat.id} value={subCat.id}>
                      {subCat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Assign this item to a sub-category for better organization
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.has_variants}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    setFormData({
                      ...formData,
                      has_variants: checked,
                      variants: checked
                        ? formData.variants || [
                            {
                              name: "",
                              dine_in: "",
                              takeaway: "",
                              delivery: "",
                            },
                          ]
                        : [],
                    });
                  }}
                  className="w-4 h-4"
                />
                <Label>This item has multiple sizes/prices</Label>
              </div>

              {!formData.has_variants ? (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Dine-in Price ({formatCurrency(0)}) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.dine_in}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pricing: {
                            ...formData.pricing,
                            dine_in: e.target.value,
                          },
                        })
                      }
                    />
                    {formData.pricing.dine_in !== "" && (
                      <p className="text-xs text-slate-500 mt-1">
                        Commission included: {formatCurrency(applyCommission(formData.pricing.dine_in))}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Takeaway Price ({formatCurrency(0)}) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.takeaway}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pricing: {
                            ...formData.pricing,
                            takeaway: e.target.value,
                          },
                        })
                      }
                    />
                    {formData.pricing.takeaway !== "" && (
                      <p className="text-xs text-slate-500 mt-1">
                        Commission included: {formatCurrency(applyCommission(formData.pricing.takeaway))}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Online Price ({formatCurrency(0)})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.pricing.delivery}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          pricing: {
                            ...formData.pricing,
                            delivery: e.target.value,
                          },
                        })
                      }
                    />
                    {formData.pricing.delivery !== "" && (
                      <p className="text-xs text-slate-500 mt-1">
                        Commission included: {formatCurrency(applyCommission(formData.pricing.delivery))}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(formData.variants || []).map((variant, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="grid grid-cols-4 gap-3">
                        <Input
                          placeholder="Size / Quantity"
                          value={variant.name}
                          onChange={(e) => {
                            const updated = [...formData.variants];
                            updated[index].name = e.target.value;
                            setFormData({
                              ...formData,
                              variants: updated,
                            });
                          }}
                        />

                        <Input
                          type="number"
                          placeholder="Dine In"
                          value={variant.dine_in}
                          onChange={(e) => {
                            const updated = [...formData.variants];
                            updated[index].dine_in = e.target.value;
                            setFormData({
                              ...formData,
                              variants: updated,
                            });
                          }}
                        />

                        <Input
                          type="number"
                          placeholder="Takeaway"
                          value={variant.takeaway}
                          onChange={(e) => {
                            const updated = [...formData.variants];
                            updated[index].takeaway = e.target.value;
                            setFormData({
                              ...formData,
                              variants: updated,
                            });
                          }}
                        />

                        <Input
                          type="number"
                          placeholder="Delivery"
                          value={variant.delivery}
                          onChange={(e) => {
                            const updated = [...formData.variants];
                            updated[index].delivery = e.target.value;
                            setFormData({
                              ...formData,
                              variants: updated,
                            });
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-xs text-slate-500 mt-2">
                        <div />
                        <div>
                          {variant.dine_in !== "" && (
                            <p>
                              Commission included: {formatCurrency(applyCommission(variant.dine_in))}
                            </p>
                          )}
                        </div>
                        <div>
                          {variant.takeaway !== "" && (
                            <p>
                              Commission included: {formatCurrency(applyCommission(variant.takeaway))}
                            </p>
                          )}
                        </div>
                        <div>
                          {variant.delivery !== "" && (
                            <p>
                              Commission included: {formatCurrency(applyCommission(variant.delivery))}
                            </p>
                          )}
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="destructive"
                        className="mt-2"
                        onClick={() => {
                          const updated = formData.variants.filter(
                            (_, i) => i !== index
                          );
                          setFormData({
                            ...formData,
                            variants: updated,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}

                  <Button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        variants: [
                          ...formData.variants,
                          {
                            name: "",
                            dine_in: "",
                            takeaway: "",
                            delivery: "",
                          },
                        ],
                      })
                    }
                  >
                    Add Variant
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Tax (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.tax}
                    onChange={(e) =>
                      setFormData({ ...formData, tax: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="edit-availability"
                    checked={formData.availability}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        availability: e.target.checked,
                      })
                    }
                    className="w-4 h-4"
                  />
                  <Label htmlFor="edit-availability">Available</Label>
                </div>
              </div>
              {/* Order Type Visibility */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Order Type Visibility</h3>

                <div className="grid grid-cols-3 gap-4">
                  {/* Dine In */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility?.dine_in ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            dine_in: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Dine-In</span>
                  </label>

                  {/* Takeaway */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility?.takeaway ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            takeaway: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Takeaway</span>
                  </label>

                  {/* Delivery */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.order_type_visibility?.delivery ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          order_type_visibility: {
                            ...formData.order_type_visibility,
                            delivery: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Online</span>
                  </label>
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Control where this menu item should appear in POS.
                </p>
              </div>
              {/* Inheritance Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Inherit Modifiers & Add-Ons</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Category Modifiers */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.inheritance?.category_modifiers ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inheritance: {
                            ...formData.inheritance,
                            category_modifiers: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Category Modifiers</span>
                  </label>

                  {/* Category Add-Ons */}
                  <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.inheritance?.category_addons ?? true}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          inheritance: {
                            ...formData.inheritance,
                            category_addons: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Category Add-Ons</span>
                  </label>

                  {/* Sub-Category Modifiers - Only shown if sub-category is selected */}
                  {formData.sub_category_id && (
                    <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.inheritance?.subcategory_modifiers ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            inheritance: {
                              ...formData.inheritance,
                              subcategory_modifiers: e.target.checked,
                            },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Sub-Category Modifiers</span>
                    </label>
                  )}

                  {/* Sub-Category Add-Ons - Only shown if sub-category is selected */}
                  {formData.sub_category_id && (
                    <label className="flex items-center gap-2 border rounded-lg px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.inheritance?.subcategory_addons ?? true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            inheritance: {
                              ...formData.inheritance,
                              subcategory_addons: e.target.checked,
                            },
                          })
                        }
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-medium">Sub-Category Add-Ons</span>
                    </label>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  Allow this item to inherit modifiers and add-ons from its category/sub-category.
                </p>
              </div>
              {/* Kitchen Settings */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Kitchen Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Course Type</Label>
                    <select
                      value={formData.course_type_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          course_type_id: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select course type</option>
                      {courseTypes.map((ct) => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Prep Time (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="120"
                      value={formData.prep_time_minutes}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          prep_time_minutes: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modifier Groups */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Modifier Groups</h3>
                <Button type="button" size="sm" onClick={addModifierGroup}>
                  <Plus size={16} className="mr-2" />
                  Add Group
                </Button>
              </div>

              {formData.modifier_groups.map((group, groupIndex) => (
                <Card key={`edit-group-${groupIndex}`} className="mb-4">
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 space-y-3">
                          <Input
                            placeholder="Group name (e.g., Spice Level)"
                            value={group.name}
                            onChange={(e) =>
                              updateModifierGroup(
                                groupIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.required}
                                onChange={(e) =>
                                  updateModifierGroup(
                                    groupIndex,
                                    "required",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Required</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={group.multi_select}
                                onChange={(e) =>
                                  updateModifierGroup(
                                    groupIndex,
                                    "multi_select",
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="text-sm">Multi-select</span>
                            </label>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeModifierGroup(groupIndex)}
                        >
                          <X size={16} />
                        </Button>
                      </div>

                      {/* Modifier Options */}
                      <div className="ml-4 space-y-2">
                        {group.options.map((option, optionIndex) => (
                          <div
                            key={`edit-option-${groupIndex}-${optionIndex}`}
                            className="flex gap-2"
                          >
                            <Input
                              placeholder="Option name"
                              value={option.name}
                              onChange={(e) =>
                                updateModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="flex-1"
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Price"
                              value={option.price}
                              onChange={(e) =>
                                updateModifierOption(
                                  groupIndex,
                                  optionIndex,
                                  "price",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-24"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeModifierOption(groupIndex, optionIndex)
                              }
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addModifierOption(groupIndex)}
                        >
                          <Plus size={14} className="mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Add-Ons */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Add-Ons</h3>
                <Button type="button" size="sm" onClick={addAddOn}>
                  <Plus size={16} className="mr-2" />
                  Add Add-On
                </Button>
              </div>

              <div className="space-y-2">
                {formData.add_ons.map((addOn, index) => (
                  <div key={`edit-addon-${index}`} className="flex gap-2">
                    <Input
                      placeholder="Add-on name"
                      value={addOn.name}
                      onChange={(e) =>
                        updateAddOn(index, "name", e.target.value)
                      }
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={addOn.price}
                      onChange={(e) =>
                        updateAddOn(
                          index,
                          "price",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAddOn(index)}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              className="bg-red-600 hover:bg-red-700"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
